/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { appVersions, workflowVersionsList, workflows } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { activeThreadWorkflowId, rawActiveThreadDraftWorkflow, rawAppsMetadataItems } from '@/redux/selectors/pieveSelectors/composerResolutionSelectors.js';
import buildStepChangeMarks from '@/redux/selectors/pieveSelectors/lib/buildStepChangeMarks.js';
import diffWorkflowSnapshotSteps from '@/redux/selectors/pieveSelectors/lib/diffWorkflowSnapshotSteps.js';
import hydrateWorkflowStepApps from '@/redux/selectors/pieveSelectors/lib/hydrateWorkflowStepApps.js';
import resolveLatestPublishedWorkflowVersion from '@/redux/selectors/pieveSelectors/lib/resolveLatestPublishedWorkflowVersion.js';
import { query } from '@/redux/selectors/uiSelectors.js';

// The active workflow's published versions, newest first (the order the server lists them in).
export const activeThreadWorkflowVersions = createSelector([workflowVersionsList, activeThreadWorkflowId], (versions, workflowId) => {
  if (!workflowId) {
    return EMPTY_ARRAY;
  }

  return versions.filter((version) => String(version?.workflowId || '').trim() === workflowId);
});

export const activeThreadWorkflowLatestPublishedVersion = createSelector([activeThreadWorkflowVersions], (versions) => {
  return resolveLatestPublishedWorkflowVersion(versions);
});

// The number the next publish or restore will get. Versions are numbered by the server from the highest one there is,
// and the list is newest first.
export const activeThreadWorkflowNextVersion = createSelector([activeThreadWorkflowVersions], (versions) => {
  return (Number(versions[0]?.version) || 0) + 1;
});

// The published snapshot is a separate row from the draft (publish inserts a new row), so both
// coexist in the canonical workflows array and are told apart by `version`.
export const activeThreadPublishedWorkflow = createSelector([workflows, activeThreadWorkflowId, activeThreadWorkflowLatestPublishedVersion], (workflowList, workflowId, latestPublished) => {
  const version = String(latestPublished?.version || '').trim();

  if (!workflowId || !version) {
    return null;
  }

  return workflowList.find((item) => item?.workflowId === workflowId && String(item?.version || '').trim() === version) || null;
});

// What the draft would change if it were published now, step by step. `null` until the published snapshot has
// loaded, and when nothing is published yet — there is nothing to compare with, and the caller says so instead.
export const activeThreadWorkflowDraftChanges = createSelector([activeThreadPublishedWorkflow, rawActiveThreadDraftWorkflow, rawAppsMetadataItems], (published, draft, appsMetadataItems) => {
  return diffWorkflowSnapshotSteps({ before: published, after: draft, appsMetadataItems });
});

// Version history is a view of the workflow surface with its own URL (`?version=6`), so a version can be pasted to
// someone. `null` while the surface shows the draft.
export const activeThreadWorkflowHistoryVersion = (state) => String(query(state, 'version') || '').trim() || null;

// The history row being viewed: who published it, when, why, and how its runs went.
export const activeThreadWorkflowHistoryRow = createSelector([activeThreadWorkflowVersions, activeThreadWorkflowHistoryVersion], (versions, version) => {
  return versions.find((row) => row?.version === version) || null;
});

// The version before the viewed one, which is what "what this version changed" is measured against.
export const activeThreadWorkflowHistoryPreviousVersion = createSelector([activeThreadWorkflowVersions, activeThreadWorkflowHistoryVersion], (versions, version) => {
  const index = versions.findIndex((row) => row?.version === version);

  return index >= 0 ? versions[index + 1]?.version || null : null;
});

export const activeThreadWorkflowHistorySnapshot = createSelector([workflows, activeThreadWorkflowId, activeThreadWorkflowHistoryVersion], (workflowList, workflowId, version) => {
  return version ? workflowList.find((item) => item?.workflowId === workflowId && item?.version === version) || null : null;
});

const activeThreadWorkflowHistoryPreviousSnapshot = createSelector([workflows, activeThreadWorkflowId, activeThreadWorkflowHistoryPreviousVersion], (workflowList, workflowId, version) => {
  return version ? workflowList.find((item) => item?.workflowId === workflowId && item?.version === version) || null : null;
});

// The viewed version's steps, drawn with their apps like the draft's.
export const activeThreadWorkflowHistorySteps = createSelector([activeThreadWorkflowHistorySnapshot, rawAppsMetadataItems], (snapshot, appsMetadataItems) => {
  return snapshot ? hydrateWorkflowStepApps({ steps: snapshot.definition.steps, appsMetadataItems, appMetadataById: snapshot.appMetadataById }) : EMPTY_ARRAY;
});

// What the viewed version changed from the one before it. `null` for the first version and while either loads.
export const activeThreadWorkflowHistoryChanges = createSelector([activeThreadWorkflowHistoryPreviousSnapshot, activeThreadWorkflowHistorySnapshot, rawAppsMetadataItems], (previous, snapshot, appsMetadataItems) => {
  return diffWorkflowSnapshotSteps({ before: previous, after: snapshot, appsMetadataItems });
});

export const activeThreadWorkflowHistoryStepMarks = createSelector([activeThreadWorkflowHistoryChanges], (changes) => {
  return buildStepChangeMarks(changes);
});

// What restoring the viewed version would change in what runs: measured against the published version, not the draft.
export const activeThreadWorkflowRestoreChanges = createSelector([activeThreadPublishedWorkflow, activeThreadWorkflowHistorySnapshot, rawAppsMetadataItems], (published, snapshot, appsMetadataItems) => {
  return diffWorkflowSnapshotSteps({ before: published, after: snapshot, appsMetadataItems });
});

// One app's versions as the step popover's version choices, newest first: the published one and the unpublished draft
// say so, every other one says when it was published.
export const appVersionOptions = createSelector([appVersions, (_state, appId) => appId], (rows, appId) => {
  return rows
    .filter((row) => row.appId === appId)
    .map((row) => ({
      value: row.version,
      label: row.version === 'draft' ? 'Draft' : `v${row.version}`,
      trailing: row.isPublished ? 'Published' : row.version === 'draft' ? 'Unpublished' : new Date(row.createdOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }));
});
